import { SEZIONI, SEZIONI_AWG } from '../constants/cableConstants';
import { getTerminalData } from './cableLogic';
import { DEFAULT_PEEL_FOR_UNMARKED_CABLES } from '../constants/terminalConstants';

// Funzioni di conversione
export const convertMetricToAWG = (metricValue) => {
  const awgSection = SEZIONI_AWG.find(s => s.metric === metricValue);
  return awgSection ? awgSection.val : metricValue;
};

export const convertAWGToMetric = (awgValue) => {
  const awgSection = SEZIONI_AWG.find(s => s.val === awgValue);
  return awgSection ? awgSection.metric : awgValue;
};

// Funzioni per la siglatura e collegamento (da original HTML)
export const getSiglaturaPerStampa = (cable, settings) => {
  if (settings.marcaturePerCollegamento) {
    const sigA = extractSiglaturaFromCollegamento(cable.collegamentoA);
    const sigB = extractSiglaturaFromCollegamento(cable.collegamentoB);

    if (sigA && sigB && sigA === sigB) return sigA;
    if (sigA && sigB && sigA !== sigB) return `${sigA}-${sigB}`;
    return sigA || sigB || '';
  }
  return cable.siglatura;
};

export const extractSiglaturaFromCollegamento = (collegamento) => {
  if (!collegamento) return '';
  const match = collegamento.match(/^(.*?)(?:\s*\(|[\s\[]+([0-9]+)\s*\)|[\s\[]+([A-Za-z])\s*\])?(?:\s*(?:▲|▼|→|←|↔))?$/);
  if (match) {
    // Se il primo gruppo cattura la sigla principale (es. 'F1.1')
    if (match[1]) return match[1].trim();
    // Se il secondo o terzo gruppo cattura numeri/lettere (es. '1', 'A')
    if (match[2]) return match[2].trim();
    if (match[3]) return match[3].trim();
  }
  return collegamento.replace(/\s*\(.*\)/, '').replace(/\s*\d+$/, '').replace(/[▲▼→←↔]/g, '').trim(); // Fallback per pulizia
};

export const generateACPowerData = (cables, settings, terminali, getSortedCablesForList, setWarnings, exportForEW3035) => {
  const missing = [];

  const standardCables = cables.filter(cable => !(cable.poli && Array.isArray(cable.poli)));

  // Filtra i cavi supportati (convertendo AWG in mm² per il controllo)
  const allSortedCables = getSortedCablesForList(cables); // ← Usa TUTTI i cavi
  const sortedCables = allSortedCables.filter(cable => {
    // Escludi i multipolari
    if (cable.poli && Array.isArray(cable.poli)) {
      return false;
    }

    // Filtra per sezione supportata
    const sezioneInMM = settings.useAWG ? convertAWGToMetric(cable.sezione) : cable.sezione;
    return sezioneInMM <= 6;
  });

  // Trova i cavi esclusi (convertendo AWG in mm² per il controllo) - SOLO TRA QUELLI STANDARD
  const excludedCables = standardCables.filter(cable => {
    const sezioneInMM = settings.useAWG ? convertAWGToMetric(cable.sezione) : cable.sezione;
    return sezioneInMM > 6;
  });

  // Info sui cavi multipolari esclusi
  const multipolarCables = cables.filter(cable => cable.poli && Array.isArray(cable.poli));
  if (multipolarCables.length > 0) {
    const multipolarDetails = multipolarCables.map(c => {
      const name = c.nomeCavo ? ` \"${c.nomeCavo}\"` : '';
      return `#${c.originalIndex + 1}${name} (${c.poli.length} poli)`;
    }).join(', ');
    missing.push(`Info: ${multipolarCables.length} cavi multipolari esclusi da AC_Power: ${multipolarDetails}`);
  }

  // Warning dettagliato basato sulla modalità - SOLO PER CAVI STANDARD
  if (excludedCables.length > 0) {
    let excludedInfo;

    if (settings.useAWG) {
      const excludedDetails = excludedCables.map(c => {
        const sigla = c.siglatura ? ` \"${c.siglatura}\"` : '';
        return `#${c.originalIndex + 1}${sigla} (${c.sezione} AWG)`;
      }).join(', ');
      excludedInfo = `Info: ${excludedCables.length} cavi AWG con sezione > 6mm² esclusi da AC_Power: ${excludedDetails}`;
    } else {
      const excludedDetails = excludedCables.map(c => {
        const sigla = c.siglatura ? ` \"${c.siglatura}\"` : '';
        return `#${c.originalIndex + 1}${sigla} (${c.sezione}mm²)`;
      }).join(', ');
      excludedInfo = `Info: ${excludedCables.length} cavi > 6mm² esclusi da AC_Power: ${excludedDetails}`;
    }

    missing.push(excludedInfo);
  }

  // ✅ NUOVA LOGICA: Compensazione lunghezza per cavi consecutivi
  let processedCables = [...sortedCables];
  let compensatedCount = 0;

  if (settings.compensazioneLunghezza && settings.marcaCavi === 1) {
    // Identifica gruppi consecutivi con stessa lunghezza E stesso colore
    for (let i = 0; i < processedCables.length; i++) {
      const currentCable = processedCables[i];
      const consecutiveGroup = [i]; // Indici del gruppo consecutivo

      // Trova tutti i cavi consecutivi con stessa lunghezza e colore
      for (let j = i + 1; j < processedCables.length; j++) {
        const nextCable = processedCables[j];
        if (nextCable.lunghezza === currentCable.lunghezza &&
          nextCable.colore === currentCable.colore) {
          consecutiveGroup.push(j);
        } else {
          break; // Interrompi alla prima differenza
        }
      }

      // ✅ NUOVA LOGICA: Se tutti i cavi del gruppo hanno la stessa siglatura, 
      // non applicare compensazione per evitare scarti
      const allSameSiglatura = consecutiveGroup.every(cableIndex => 
        processedCables[cableIndex].siglatura === currentCable.siglatura
      );

      // Se il gruppo ha più di 1 cavo, applica compensazione ottimizzata
      if (consecutiveGroup.length > 1 && !allSameSiglatura) {
        let lastUsedLength = currentCable.lunghezza; // Lunghezza del primo cavo (invariata)

        consecutiveGroup.forEach((cableIndex, groupPos) => {
          if (groupPos === 0) {
            // Primo cavo: sempre invariato
            processedCables[cableIndex] = {
              ...processedCables[cableIndex],
              lunghezzaACPower: processedCables[cableIndex].lunghezza
            };
            lastUsedLength = processedCables[cableIndex].lunghezza;
          } else {
            // Cavi successivi: usa la lunghezza minima diversa dalla precedente
            let newLength;
            if (lastUsedLength === currentCable.lunghezza) {
              // Se il precedente era originale, usa +1mm
              newLength = currentCable.lunghezza + 1;
            } else {
              // Se il precedente era +1mm, torna all'originale
              newLength = currentCable.lunghezza;
            }

            processedCables[cableIndex] = {
              ...processedCables[cableIndex],
              lunghezzaACPower: newLength
            };
            lastUsedLength = newLength;

            if (newLength !== currentCable.lunghezza) {
              compensatedCount++;
            }
          }
        });

        // Salta i cavi già processati
        i = consecutiveGroup[consecutiveGroup.length - 1];
      } else if (consecutiveGroup.length > 1 && allSameSiglatura) {
        // ✅ NUOVO: Gruppo con stessa siglatura - nessuna compensazione per evitare scarti
        consecutiveGroup.forEach(cableIndex => {
          processedCables[cableIndex] = {
            ...processedCables[cableIndex],
            lunghezzaACPower: processedCables[cableIndex].lunghezza
          };
        });
        // Salta i cavi già processati
        i = consecutiveGroup[consecutiveGroup.length - 1];
      } else {
        // Cavo singolo: nessuna compensazione
        processedCables[i] = {
          ...processedCables[i],
          lunghezzaACPower: processedCables[i].lunghezza
        };
      }
    }
  } else {
    // Nessuna compensazione: usa lunghezza originale
    processedCables = processedCables.map(cable => ({
      ...cable,
      lunghezzaACPower: cable.lunghezza
    }));
  }

  const data = processedCables.map((cable, index) => {
    const sez = (settings.useAWG ? SEZIONI_AWG : SEZIONI).find(s => s.val === cable.sezione) || {};
    let termA = getTerminalData(cable.terminaleA, settings, terminali);
    let termB = getTerminalData(cable.terminaleB, settings, terminali);

    if (!cable.terminaleA || cable.terminaleA.trim() === '') {
      missing.push(`Terminale A mancante (cavo #${cable.siglatura || index + 1})`);
      termA = { len: 0, peel: 0, nome: '', printOffset: 0 };
    } else if (!termA) {
      missing.push(`Terminale A \"${cable.terminaleA}\" non definito (cavo #${cable.siglatura || index + 1})`);
      termA = { len: 0, peel: 0, nome: '', printOffset: 0 };
    }

    if (!cable.terminaleB || cable.terminaleB.trim() === '') {
      missing.push(`Terminale B mancante (cavo #${cable.siglatura || index + 1})`);
      termB = { len: 0, peel: 0, nome: '', printOffset: 0 };
    } else if (!termB) {
      missing.push(`Terminale B \"${cable.terminaleB}\" non definito (cavo #${cable.siglatura || index + 1})`);
      termB = { len: 0, peel: 0, nome: '', printOffset: 0 };
    }

    // Logica per forzare cavi neri
    const getProductColor = (originalColor) => {
      if (!settings.forceBlackCables) {
        return originalColor;
      }

      const isEarthCable = originalColor === 'GI/VE' || originalColor === 'YE/GN';
      if (isEarthCable) {
        return originalColor;
      } else {
        return settings.lingua === 'it' ? 'NERO' : 'BLACK';
      }
    };

    // ✅ Escludi cavi con sezione < 0.5 dalla marcatura
    const shouldMark = cable.sezione >= 0.5;
    const hasSiglatura = shouldMark && (!settings.marcaturePerCollegamento ? (cable.siglatura && String(cable.siglatura).trim() !== '') : (getSiglaturaPerStampa(cable, settings) && String(getSiglaturaPerStampa(cable, settings)).trim() !== ''));

    let midContent = '';
    if (settings.marcaturaCentrale === 'siglatura') {
      midContent = cable.siglatura || (index + 1);
    } else if (settings.marcaturaCentrale === 'numerazione') {
      midContent = index + 1;
    }

    let head2Content = '';
    let tail2Content = '';

    if (settings.abilitaMarcature2) {
      if (settings.tipoMarcature2 === 'siglatura') {
        head2Content = cable.siglatura || '';
        tail2Content = cable.siglatura || '';
      } else {
        head2Content = cable.collegamentoA || '';
        tail2Content = cable.collegamentoB || '';
      }
    }

    const loopType = settings.loopMode === 'numero' ? 0 : 1;
    const siglaturaPerMarcature = settings.marcaturePerCollegamento ? getSiglaturaPerStampa(cable, settings) : cable.siglatura;

    // ✅ Logica per riduzione peel quando marcatura non attiva e cavo ha siglatura
    const shouldReducePeel = settings.reducePeelForManualMarking && 
                             settings.marcaCavi === 0 && 
                             hasSiglatura;
    
    // Se il peel è già <= DEFAULT_PEEL_FOR_UNMARKED_CABLES, mantienilo così com'è, altrimenti riducilo al valore standard
    const peelF1Value = shouldReducePeel ? Math.min(termA.peel, DEFAULT_PEEL_FOR_UNMARKED_CABLES) : termA.peel;
    const peelB1Value = shouldReducePeel ? Math.min(termB.peel, DEFAULT_PEEL_FOR_UNMARKED_CABLES) : termB.peel;

    return {
      num: `#${cable.originalIndex + 1}`,
      orderNo: cable.siglatura || '',
      product: getProductColor(cable.colore),
      wireSize: sez.val,
      totalSet: settings.quantitaCablaggi,
      tFinish: "0",
      length: cable.lunghezzaACPower,
      lenF1: termA.len,
      peelF1: peelF1Value,
      lenB1: termB.len,
      peelB1: peelB1Value,
      dia1: sez.dia,
      back1: sez.back1,
      clampGapL: sez.clampGapL || 4,
      clampGapR: sez.clampGapR || 4,
      // 8 colonne vuote (P-W)
      empty1: 0, empty2: 0, empty3: 0, empty4: 0,
      empty5: 0, empty6: 0, empty7: 0, empty8: 0,
      // Resto delle colonne
      charsSend: hasSiglatura ? settings.marcaCavi : 0,
      wordSize: sez.wordSize,
      boldSize: settings.grandezzaGrassetto,
      head1Content: hasSiglatura ? siglaturaPerMarcature : '',
      flip1: settings.ribaltaTesta,
      head2Content: head2Content,
      flip2: settings.ribaltaTesta,
      midContent: midContent,
      flip3: settings.ribaltaCentrale,
      tail2Content: tail2Content,
      flip4: settings.ribaltaCoda,
      tail1Content: hasSiglatura ? siglaturaPerMarcature : '',
      flip5: settings.ribaltaCoda,
      printing: hasSiglatura ? settings.marcaCavi : 0,
      head1PrintPos: hasSiglatura ? settings.posMarcaturaTesta + (termA.printOffset || 0) : 0,
      head2PrintPos: (hasSiglatura && settings.abilitaMarcature2) ? settings.posMarcaturaTesta + settings.distanzaMarcature2 + (termA.printOffset || 0) : 0,
      printLoopType: loopType,
      printLoopNum: settings.marcaturaCentrale !== 'niente' ? settings.loopNum : 0,
      printLoopDist: settings.loopDist,
      tail2PrintPos: (hasSiglatura && settings.abilitaMarcature2) ? settings.posMarcaturaCoda + settings.distanzaMarcature2 + (termB.printOffset || 0) : 0,
      tail1PrintPos: hasSiglatura ? settings.posMarcaturaCoda + (termB.printOffset || 0) : 0
    };
  });

  // ✅ AGGIUNGI WARNING se compensazione è attiva
  if (settings.compensazioneLunghezza && settings.marcaCavi === 1 && compensatedCount > 0) {
    missing.push(`Info: ${compensatedCount} cavi hanno ricevuto compensazione lunghezza per evitare conflitti nella macchinetta`);
  }
  if (settings.compensazioneLunghezza && settings.marcaCavi === 1) {
    missing.push(`Info: La compensazione è ottimizzata per ridurre gli scarti (cavi con stessa siglatura non vengono compensati)`);
  }
  
  // ✅ AGGIUNGI INFO sui cavi esclusi dalla marcatura
  const excludedFromMarking = processedCables.filter(cable => cable.sezione < 0.5).length;
  if (excludedFromMarking > 0) {
    missing.push(`Info: ${excludedFromMarking} cavi con sezione < 0.5mm² sono stati esclusi dalla marcatura`);
  }
  if (settings.forceBlackCables) {
    const earthCables = processedCables.filter(cable =>
      cable.colore === 'GI/VE' || cable.colore === 'YE/GN'
    ).length;
    const blackCables = processedCables.length - earthCables;

    if (blackCables > 0) {
      missing.push(`Info: ${blackCables} cavi forzati a NERO nel foglio AC_Power (${earthCables} terre preservate)`);
    }
  }

  setWarnings(missing);
  return data;
};
